import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import React, { useState } from 'react';
import { usePermissions } from 'react-admin';
import { DEFAULT_API_ADMIN, PERMISSIONS } from '../../constants';
import { httpClientReactAdmin } from '../../lib';

/**
 * Botão "Tentar agora": refaz a chamada ao integrador do cupom AO VIVO — com o
 * token atual do posto e o código externo (id_brinde do RTI / serviço da Trevo)
 * relido do banco — em vez de reaproveitar o payload congelado da falha.
 *
 * É diagnóstico: não gera cupom no PontuaX, não vincula cliente e não entra no
 * relatório de falhas. A ressalva é o RTI, cujo único endpoint é o de gerar
 * cupom — teste que dá certo cria um cupom de verdade no parceiro (por isso o
 * aviso no diálogo).
 *
 * Só aparece para admin.
 */

interface Attempt {
  label: string;
  url: string | null;
  requestBody: any;
  responseBody: any;
  httpStatus: number | null;
  errorCode: string | null;
  errorMessage: string | null;
  durationMs: number;
  ok: boolean;
}

interface TestResult {
  ok: boolean;
  integration: string;
  message: string;
  diagnosis: string | null;
  place: { id: string; name: string; cnpj: string; chainId: string | null; chainName: string | null };
  resolved: {
    externalCode: number | null;
    externalCodeSource: string | null;
    token: string | null;
  };
  attempts: Attempt[];
}

interface Props {
  /** Retentativa a partir de uma falha do relatório. */
  logId?: string | null;
  placeId?: string | null;
  productId?: string | null;
  giftProductId?: string | null;
  integration?: string | null;
  /** Código ainda não salvo no formulário (aba Resgate) — sobrepõe o do banco. */
  externalCode?: number | string | null;
  label?: string;
  /** `icon` = botão compacto de linha de formulário. */
  compact?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  informado: 'valor do formulário',
  gift_product_place: 'configuração do brinde no posto',
  product_place: 'configuração do produto no posto',
};

const CODE_LABEL: Record<string, string> = {
  rti: 'Código RTI (id_brinde)',
  trevo: 'Código de serviço Trevo',
};

export const CouponIntegrationTestButton: React.FC<Props> = ({
  logId,
  placeId,
  productId,
  giftProductId,
  integration,
  externalCode,
  label = 'Tentar agora',
  compact = false,
  disabled = false,
  disabledReason,
}) => {
  const { permissions } = usePermissions();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cpf, setCpf] = useState('');

  if (!permissions?.includes(PERMISSIONS.ADMIN)) return null;
  if (!logId && !placeId) return null;

  const isRti = integration === 'rti';
  const needsCpf = integration === 'trevo' && !logId;

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/admin/coupon-integration/test`, {
        method: 'POST',
        body: JSON.stringify({
          logId: logId || undefined,
          placeId: placeId || undefined,
          productId: productId || undefined,
          giftProductId: giftProductId || undefined,
          integration: integration || undefined,
          externalCode: externalCode === '' || externalCode === undefined ? undefined : externalCode,
          cpf: cpf || undefined,
        }),
      });
      setResult((json?.data?.[0] ?? json) as TestResult);
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Erro ao testar a integração');
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setResult(null);
    setError(null);
    setOpen(true);
  };

  const button = compact ? (
    <Button
      size="small"
      variant="outlined"
      startIcon={<PlayArrowIcon />}
      onClick={openDialog}
      disabled={disabled}
      sx={{ textTransform: 'none', alignSelf: 'center', height: 40 }}
    >
      Testar
    </Button>
  ) : (
    <Button size="small" variant="outlined" onClick={openDialog} disabled={disabled} sx={{ textTransform: 'none' }}>
      {label}
    </Button>
  );

  return (
    <>
      {disabled && disabledReason ? <Tooltip title={disabledReason}><span>{button}</span></Tooltip> : button}

      <Dialog open={open} onClose={loading ? undefined : () => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Testar integração agora</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Refaz a chamada ao parceiro com o token atual do posto e o código relido do cadastro — nada do payload
            antigo é reaproveitado. Não gera cupom no PontuaX nem entra no relatório de falhas.
          </Typography>

          {isRti && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              O RTI só tem o endpoint de gerar cupom: se o teste passar, um cupom de verdade é criado lá.
            </Alert>
          )}

          {needsCpf && (
            <TextField
              size="small"
              label="CPF para o teste"
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              helperText="A Trevo consulta por cliente, então o teste precisa de um CPF."
              sx={{ mb: 2 }}
            />
          )}

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {result && (
            <Box>
              <Alert severity={result.ok ? 'success' : 'error'} sx={{ mb: 2 }}>
                {result.message}
              </Alert>

              {result.diagnosis && (
                <Alert severity="info" sx={{ mb: 2 }}>{result.diagnosis}</Alert>
              )}

              <Box sx={{ mb: 2 }}>
                <InfoRow label="Posto" value={`${result.place.name}${result.place.chainName ? ` — ${result.place.chainName}` : ''}`} />
                <InfoRow label="CNPJ" value={result.place.cnpj} />
                {result.resolved.token && <InfoRow label="Token usado" value={result.resolved.token} mono />}
                {result.resolved.externalCode != null && (
                  <InfoRow
                    label={CODE_LABEL[result.integration] || 'Código externo'}
                    value={`${result.resolved.externalCode}${result.resolved.externalCodeSource ? ` (${SOURCE_LABELS[result.resolved.externalCodeSource] || result.resolved.externalCodeSource})` : ''}`}
                    mono
                  />
                )}
              </Box>

              {result.attempts.map((attempt, index) => (
                <Box key={index} sx={{ mb: 2, border: '1px solid #e2e8f0', borderRadius: 1, p: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={attempt.ok ? 'OK' : 'Falhou'}
                      sx={{ bgcolor: attempt.ok ? '#16a34a' : '#dc2626', color: '#fff', fontWeight: 700 }}
                    />
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{attempt.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {attempt.durationMs}ms
                      {attempt.httpStatus != null ? ` · HTTP ${attempt.httpStatus}` : ''}
                    </Typography>
                  </Box>
                  {attempt.url && (
                    <Typography variant="caption" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', display: 'block', mb: 1 }}>
                      {attempt.url}
                    </Typography>
                  )}
                  <PayloadBlock label="Enviado (request)" value={attempt.requestBody} />
                  <PayloadBlock label="Recebido (response)" value={attempt.responseBody} />
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={loading}>Fechar</Button>
          <Button variant="contained" onClick={run} disabled={loading || (needsCpf && !cpf)}>
            {loading ? <CircularProgress size={20} /> : result ? 'Testar de novo' : 'Testar agora'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', mb: 0.5 }}>
    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, minWidth: 150 }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-all' }}>{value}</Typography>
  </Box>
);

const PayloadBlock: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>{label}</Typography>
    <Box
      component="pre"
      sx={{
        m: 0, mt: 0.5, p: 1.5, bgcolor: '#0f172a', color: '#e2e8f0', borderRadius: 1,
        fontSize: 12, overflow: 'auto', maxHeight: 240, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}
    >
      {value == null ? '—' : JSON.stringify(value, null, 2)}
    </Box>
  </Box>
);

export default CouponIntegrationTestButton;
